import { useQuery } from '@tanstack/react-query';
import { adminSystemApi } from '@api/client';
import type { SystemHealth, SystemError, CronJobStatus, WorkerMetrics } from '@api/types';
import * as Icons from '@components/Icons';
import { PageHead } from '@components/admin/PageHead';

type StatTone = 'neutral' | 'ok' | 'warn' | 'danger';

function latencyTone(p95Ms: number): StatTone {
  if (p95Ms < 200) return 'ok';
  if (p95Ms < 500) return 'warn';
  return 'danger';
}

function errorRateTone(percent: number): StatTone {
  if (percent < 0.5) return 'ok';
  if (percent < 2) return 'warn';
  return 'danger';
}

const CRON_LABELS = new Map<string, { desc: string; schedule: string }>([
  ['backup',              { desc: 'Backup diario completo a R2 (3:00 UTC)',                    schedule: '0 3 * * *' }],
  ['cleanup-unconfirmed', { desc: 'Cancela reservas pending sin confirmar y stuck approval',   schedule: '0 3 * * *' }],
  ['backup-6h',           { desc: 'Backup incremental cada 6h',                                schedule: '0 */6 * * *' }],
  ['verify-backup',       { desc: 'Verificación de integridad SHA-256 del último backup',      schedule: '30 3 * * *' }],
  ['purge-expired',       { desc: 'Limpieza de magic links + rate-limit + lifecycle bookings', schedule: '*/15 * * * *' }],
  ['dead-mans-switch',    { desc: 'Heartbeat a monitor externo si los backups dejan de pasar', schedule: '*/5 * * * *' }],
]);

function fmtSize(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${String(bytes)} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

function fmtRelative(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'ahora';
  if (mins < 60) return `hace ${String(mins)} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `hace ${String(hours)} h`;
  const days = Math.floor(hours / 24);
  return `hace ${String(days)} d`;
}

function fmtDuration(ms: number): string {
  if (ms < 1000) return `${String(ms)} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}

function StatusDot({ status }: { status: CronJobStatus['status'] }) {
  const tone = status === 'success' ? 'ok' : status === 'failure' ? 'danger' : status === 'running' ? 'warn' : 'neutral';
  const bg = tone === 'ok' ? 'oklch(0.55 0.13 155)'
    : tone === 'warn' ? 'oklch(0.62 0.13 70)'
    : tone === 'danger' ? 'var(--danger)'
    : 'var(--fg-soft)';
  return (
    <span style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: bg,
      marginRight: 8,
      verticalAlign: 'middle',
    }} />
  );
}

export function SistemaPage() {
  const { data, isLoading, error, refetch, isFetching } = useQuery<SystemHealth>({
    queryKey: ['system', 'health'],
    queryFn: () => adminSystemApi.health(),
    staleTime: 30_000,
  });

  const { data: errors } = useQuery<SystemError[]>({
    queryKey: ['system', 'errors'],
    queryFn: () => adminSystemApi.errors(20),
    staleTime: 30_000,
  });

  const { data: metrics } = useQuery<WorkerMetrics>({
    queryKey: ['system', 'metrics'],
    queryFn: () => adminSystemApi.metrics(),
    staleTime: 30_000,
  });

  const allOk = data?.crons.every((c) => c.status === 'success') ?? false;

  return (
    <div>
      <PageHead
        title="Sistema"
        sub="Salud de cron jobs, backups de D1 y emails. Solo superadmin."
        meta={
          data && (
            <span>
              <StatusDot status={allOk ? 'success' : 'failure'} />
              {allOk ? 'Todos los crons OK' : 'Atención'}
            </span>
          )
        }
        actions={
          <button className="btn" onClick={() => void refetch()} disabled={isFetching}>
            <Icons.Refresh size={14} /> {isFetching ? 'Refrescando…' : 'Refrescar'}
          </button>
        }
      />

      {isLoading && <div className="card" style={{ padding: 32, textAlign: 'center' }}><span className="spinner" /></div>}
      {error && (
        <div className="card" style={{ padding: 32, color: 'var(--fg-muted)' }}>
          No se pudo cargar el estado. {error.message}
        </div>
      )}

      {data && (
        <>
          <div className="stat-row">
            <div className="stat">
              <div className="stat-label">Crons OK</div>
              <div className="stat-value mono">
                {data.crons.filter((c) => c.status === 'success').length}/{data.crons.length}
              </div>
              <div className="stat-delta" data-tone="neutral">jobs en verde</div>
            </div>
            <div className="stat">
              <div className="stat-label">Latencia API</div>
              {metrics?.available === true ? (
                <>
                  <div className="stat-value mono">{metrics.latencyP50Ms}/{metrics.latencyP95Ms} ms</div>
                  <div className="stat-delta" data-tone={latencyTone(metrics.latencyP95Ms)}>p50 / p95 · 24h</div>
                </>
              ) : (
                <>
                  <div className="stat-value mono">—</div>
                  <div className="stat-delta" data-tone="neutral">sin datos</div>
                </>
              )}
            </div>
            <div className="stat">
              <div className="stat-label">Tasa de error</div>
              {metrics?.available === true ? (
                <>
                  <div className="stat-value mono">{metrics.errorRatePercent}%</div>
                  <div className="stat-delta" data-tone={errorRateTone(metrics.errorRatePercent)}>5xx sobre {metrics.sampleRequests.toLocaleString('es-ES')} req · 24h</div>
                </>
              ) : (
                <>
                  <div className="stat-value mono">—</div>
                  <div className="stat-delta" data-tone="neutral">sin datos</div>
                </>
              )}
            </div>
            <div className="stat">
              <div className="stat-label">Emails 24h</div>
              <div className="stat-value mono">{data.remindersLast24h}</div>
              <div className="stat-delta" data-tone="neutral">Recordatorios enviados</div>
            </div>
            <div className="stat">
              <div className="stat-label">Último backup</div>
              <div className="stat-value mono">{fmtSize(data.lastBackupBytes)}</div>
              <div className="stat-delta" data-tone="neutral">{fmtRelative(data.lastBackupIso)}</div>
            </div>
            <div className="stat">
              <div className="stat-label">Backups recientes</div>
              <div className="stat-value mono">{data.recentBackups.length}</div>
              <div className="stat-delta" data-tone="neutral">en R2</div>
            </div>
          </div>

          {/* Cron jobs */}
          <section style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em', margin: 0 }}>Cron jobs</h3>
              <span className="mono cell-muted" style={{ fontSize: 11.5 }}>
                {data.crons.filter((c) => c.status === 'success').length}/{data.crons.length} OK
              </span>
            </div>
            <div className="health-grid">
              {data.crons.map((c) => {
                const meta = CRON_LABELS.get(c.name);
                return (
                <div key={c.name} className="health-card">
                  <div className="health-head">
                    <span className="health-name"><StatusDot status={c.status} />{c.name}</span>
                    <span className="health-cron mono">{meta?.schedule ?? '—'}</span>
                  </div>
                  <div className="health-desc">{meta?.desc ?? c.name}</div>
                  <div className="health-foot">
                    <span>Última: <span className="mono">{fmtRelative(c.lastRunIso)}</span></span>
                    <span style={{ textTransform: 'uppercase', fontSize: 10 }}>{c.status}</span>
                  </div>
                  {(c.runId !== null || c.durationMs !== null) && (
                    <div className="health-foot mono" style={{ fontSize: 10, opacity: 0.65, marginTop: 2 }}>
                      {c.durationMs !== null && <span>{fmtDuration(c.durationMs)}</span>}
                      {c.runId !== null && <span>run {c.runId.slice(0, 8)}…</span>}
                    </div>
                  )}
                  {c.errorMessage && (
                    <div style={{ marginTop: 6, padding: 6, background: 'var(--danger-soft)', borderRadius: 4, fontSize: 11, color: 'var(--danger)' }}>
                      {c.errorMessage}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </section>

          {/* Observabilidad — logs y métricas nativas de Cloudflare */}
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-head">
              <span className="card-title">Observabilidad</span>
              <span className="mono cell-muted" style={{ fontSize: 11 }}>CLOUDFLARE</span>
            </div>
            <div style={{ padding: '18px 18px 22px', color: 'var(--fg-muted)', fontSize: 13, lineHeight: 1.6 }}>
              <p style={{ margin: 0 }}>
                Los KPIs <strong>Latencia API</strong> y <strong>Tasa de error</strong> de arriba vienen de la
                <span className="mono"> GraphQL Analytics API</span> de Cloudflare (dataset
                <span className="mono"> workersInvocationsAdaptive</span>, ventana 24h, cache 60s).
              </p>
              <p style={{ margin: '10px 0 0' }}>
                <strong>Para correlacionar un request</strong>: busca su <span className="mono">requestId</span> (cabecera
                <span className="mono"> x-request-id</span>) en CF <span className="mono">Workers &amp; Pages</span> →
                <span className="mono"> equipomag-bookings</span> → <span className="mono">Observability / Logs</span>.
                Cada use case emite <span className="mono">useCase.start</span> y <span className="mono">useCase.end</span> con
                <span className="mono"> {'{useCase, requestId, durationMs, outcome}'}</span>.
              </p>
              <p style={{ margin: '10px 0 0' }}>
                <strong>Para reconstruir un cron run</strong>: busca su <span className="mono">cronRunId</span>
                {' '}(columna <span className="mono">run_id</span> de los crons de arriba, mismo string en los logs)
                {' '}— todos los emits del run lo llevan junto a <span className="mono">jobName</span>,
                <span className="mono"> durationMs</span> y <span className="mono">status</span>.
              </p>
              <p style={{ margin: '10px 0 0' }}>
                En vivo: <span className="mono">wrangler tail</span>.
              </p>
            </div>
          </div>

          {/* Errores recientes (5xx) — persistidos en D1 */}
          <div className="card" style={{ marginBottom: 18 }}>
            <div className="card-head">
              <span className="card-title">Errores recientes (5xx)</span>
              <span className="mono cell-muted" style={{ fontSize: 11 }}>
                {errors && errors.length > 0 ? `${String(errors.length)} en 30 días` : '30 DÍAS'}
              </span>
            </div>
            {!errors || errors.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)', fontSize: 13 }}>
                Sin errores de servidor en los últimos 30 días.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Cuándo</th>
                    <th>Estado</th>
                    <th>Ruta</th>
                    <th>Error</th>
                    <th>Trace</th>
                  </tr>
                </thead>
                <tbody>
                  {errors.map((e) => (
                    <tr key={e.id}>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>{fmtRelative(e.occurredAt)}</td>
                      <td className="mono">{e.status}</td>
                      <td className="mono" style={{ fontSize: 11.5 }}>{e.method} {e.path ?? '—'}</td>
                      <td style={{ fontSize: 12.5 }}>
                        <span className="cell-bold">{e.code}</span>
                        {e.message ? <span style={{ color: 'var(--fg-muted)' }}> — {e.message}</span> : null}
                      </td>
                      <td className="mono cell-muted" style={{ fontSize: 10.5 }}>{e.traceId ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Backups */}
          <div className="card">
            <div className="card-head">
              <span className="card-title">Backups D1 → R2</span>
              <span className="mono cell-muted" style={{ fontSize: 11 }}>RETENCIÓN INDEFINIDA</span>
            </div>
            {data.recentBackups.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--fg-muted)' }}>
                Sin backups todavía. El cron diario corre a las 03:00 UTC.
              </div>
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Archivo</th>
                    <th className="cell-right">Tamaño</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentBackups.map((b) => (
                    <tr key={b.key}>
                      <td className="mono">{b.uploadedIso.slice(0, 19).replace('T', ' ')}</td>
                      <td className="mono cell-muted" style={{ fontSize: 11, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.key}</td>
                      <td className="cell-right mono">{fmtSize(b.sizeBytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
